/* ═══════════════════════════════════════════════════════
   SwipeHome — Feed Module
   Swipe feed, search & filters, preferences, card rendering
   ═══════════════════════════════════════════════════════ */

import { api } from './api.js';
import { navigate } from './router.js';
import {
  clearFeedState,
  getUserLocation,
  RADIUS_OPTIONS,
  restoreFeedState,
  saveFeedState,
  state,
} from './state.js';
import { hideAllPreviewSheets, onFeedRefreshed as onMapFeedRefreshed, showMap } from './mapLazy.js';
import { renderInvestorInfo, renderInvestorScoreBadge } from './investorCard.js';
import type { FeedListing, InvestmentFeedResponse, PropertyType } from '../../shared/types.js';
import type { SearchFilters } from './types/ui.js';
import {
  escapeHtml,
  formatOpenHouseLabel,
  getAllMedia,
  imgFallback,
  showToast,
} from './ui.js';

import {
  checkSwipeMilestones,
  navigateToHome,
  renderOnboardingProgressCard,
  showSwipeTutorial,
} from './profile.js';


void navigate; // referenced via inline onclick globals

// ═══════════════════════════════════════════════════════
// SCREEN 4: Buyer Preferences
// ═══════════════════════════════════════════════════════

interface BuyerPreferencesUi {
  min_price?: number | null;
  max_price?: number | null;
  min_beds?: number | null;
  min_baths?: number | null;
  city?: string | null;
  location_text?: string | null;
  radius_miles?: number | null;
  property_types?: string[] | null;
  turnkey_only?: boolean | null;
  hoa_allowed?: boolean | null;
  investor_mode?: boolean | null;
}

export function renderPreferences(): HTMLElement {
  const prefs: BuyerPreferencesUi = (state.preferences as BuyerPreferencesUi) || {};
  const div = document.createElement('div');
  div.className = 'fade-in';
  div.innerHTML = `
    <div class="screen-header">
      ${state.currentScreen === 'preferences' && state.screenStack.length > 1 ? '<button class="back-btn" onclick="goBack()"><i data-feather="arrow-left" width="18"></i></button>' : '<div style="width:44px"></div>'}
      <h1>Your Preferences</h1>
      <div style="width:44px"></div>
    </div>
    <div class="screen-padded">
      <p class="text-secondary mb-24">Tell us what matters most so we can surface listings that fit — every one verified with Photo-Truth&trade; and True Cost&trade;.</p>

      <div class="pref-section">
        <h3>Price Range</h3>
        <div class="price-range">
          <input type="number" class="input" id="pref-min-price" placeholder="Min" value="${prefs.min_price || ''}">
          <span class="price-range-divider">to</span>
          <input type="number" class="input" id="pref-max-price" placeholder="Max" value="${prefs.max_price || ''}">
        </div>
      </div>

      <div class="pref-section">
        <h3>Property Type</h3>
        <div class="pref-chips" id="pref-types">
          ${['single_family', 'condo', 'townhouse', 'multi_family'].map((t) => `
            <div class="pref-chip ${(prefs.property_types || []).includes(t) ? 'selected' : ''}" data-value="${t}" onclick="toggleChip(this)">
              ${t.replace(/_/g, ' ')}
            </div>
          `).join('')}
        </div>
      </div>

      <div class="pref-section">
        <h3>Minimum Size</h3>
        <div class="input-row">
          <div class="input-group">
            <label>Beds</label>
            <select class="input" id="pref-beds">
              <option value="">Any</option>
              ${[1, 2, 3, 4, 5].map((n) => `<option value="${n}" ${prefs.min_beds == n ? 'selected' : ''}>${n}+</option>`).join('')}
            </select>
          </div>
          <div class="input-group">
            <label>Baths</label>
            <select class="input" id="pref-baths">
              <option value="">Any</option>
              ${[1, 1.5, 2, 2.5, 3, 4].map((n) => `<option value="${n}" ${prefs.min_baths == n ? 'selected' : ''}>${n}+</option>`).join('')}
            </select>
          </div>
        </div>
      </div>

      <div class="pref-section">
        <h3>Search Area</h3>
        <div class="input-group">
          <label>City or ZIP</label>
          <input type="text" class="input" id="pref-location" placeholder="e.g. Austin, TX" value="${prefs.city || ''}">
        </div>
        <div class="input-group">
          <label>Radius (miles)</label>
          <select class="input" id="pref-radius">
            ${RADIUS_OPTIONS.map((n) => `<option value="${n}" ${(prefs.radius_miles || 25) == n ? 'selected' : ''}>${n} miles</option>`).join('')}
          </select>
        </div>
      </div>

      <div class="pref-section">
        <h3>Options</h3>
        <div class="toggle-row">
          <div>
            <div class="toggle-label">Turnkey Only</div>
            <div class="toggle-sublabel">Move-in ready homes only</div>
          </div>
          <label class="toggle">
            <input type="checkbox" id="pref-turnkey" ${prefs.turnkey_only ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="toggle-label">No HOA</div>
            <div class="toggle-sublabel">Exclude homes with HOA fees</div>
          </div>
          <label class="toggle">
            <input type="checkbox" id="pref-no-hoa" ${prefs.hoa_allowed === false ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
        <div class="toggle-row">
          <div>
            <div class="toggle-label">Investor Mode</div>
            <div class="toggle-sublabel">Prioritize foreclosures & rehab deals</div>
          </div>
          <label class="toggle">
            <input type="checkbox" id="pref-investor" ${prefs.investor_mode ? 'checked' : ''}>
            <span class="toggle-slider"></span>
          </label>
        </div>
      </div>

      <button class="btn btn-primary btn-block btn-lg mt-16 mb-24" onclick="savePreferences()">Save &amp; Start Exploring</button>
    </div>
  `;
  return div;
}

export function toggleChip(el: HTMLElement): void {
  el.classList.toggle('selected');
}

export async function savePreferences(): Promise<void> {
  const types = Array.from(
    document.querySelectorAll<HTMLElement>('#pref-types .pref-chip.selected'),
  ).map((c) => c.dataset.value || '');

  const valInt = (id: string): number | null => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    const v = el?.value;
    return v ? parseInt(v, 10) || null : null;
  };
  const valFloat = (id: string): number | null => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    const v = el?.value;
    return v ? parseFloat(v) || null : null;
  };

  const payload: BuyerPreferencesUi & Record<string, unknown> = {};
  const minP = valInt('pref-min-price');
  if (minP !== null) payload.min_price = minP;
  const maxP = valInt('pref-max-price');
  if (maxP !== null) payload.max_price = maxP;
  if (types.length > 0) payload.property_types = types;
  const beds = valInt('pref-beds');
  if (beds !== null) payload.min_beds = beds;
  const baths = valFloat('pref-baths');
  if (baths !== null) payload.min_baths = baths;
  payload.radius_miles = valInt('pref-radius') || 25;
  payload.turnkey_only = !!(document.getElementById('pref-turnkey') as HTMLInputElement | null)?.checked;
  payload.hoa_allowed = !(document.getElementById('pref-no-hoa') as HTMLInputElement | null)?.checked;
  payload.investor_mode = !!(document.getElementById('pref-investor') as HTMLInputElement | null)?.checked;

  try {
    await api('PATCH', '/users/preferences', payload);
    state.preferences = payload;
    showToast('Preferences saved!', 'success');
    navigateToHome();
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to save preferences';
    showToast(message, 'error');
  }
}

// ═══════════════════════════════════════════════════════
// SCREEN 5: Swipe Feed
// ═══════════════════════════════════════════════════════

export function renderFeed(): HTMLElement {
  const div = document.createElement('div');
  div.className = 'fade-in';
  div.style.height = '100%';
  div.style.display = 'flex';
  div.style.flexDirection = 'column';

  const f = state.searchFilters;
  const activeFilterCount = countActiveFilters(f);

  div.innerHTML = `
    <div class="screen-header">
      <div class="logo-text">swipe<span>home</span></div>
      <div class="header-actions">
        ${state.searchActive ? `<button onclick="clearSearch()" title="Clear search" style="font-size:12px;color:var(--danger);background:var(--danger-bg);border:1px solid var(--danger-border);border-radius:8px;padding:4px 10px;cursor:pointer">Clear</button>` : ''}
        <button onclick="refreshFeed()" title="Refresh"><i data-feather="refresh-cw" width="18"></i></button>
      </div>
    </div>
    <div class="search-bar-row">
      <div class="search-input-wrap">
        <i data-feather="search" width="16" class="search-icon"></i>
        <input type="text" class="search-input" id="search-location" placeholder="Search city, zip, or neighborhood" value="${escapeHtml(f.location)}" onkeydown="if(event.key==='Enter')runSearch()">
      </div>
      <button class="filter-toggle-btn${activeFilterCount > 0 ? ' filter-active' : ''}" onclick="openFilterDrawer()">
        <i data-feather="sliders" width="16"></i>
        ${activeFilterCount > 0 ? `<span class="filter-badge">${activeFilterCount}</span>` : ''}
      </button>
    </div>
    <div class="feed-view-toggle">
      <div class="feed-view-toggle-group" role="group" aria-label="View mode">
        <button class="feed-view-btn ${state.feedViewMode === 'swipe' ? 'active' : ''}" data-view="swipe" onclick="switchFeedView('swipe')" aria-pressed="${state.feedViewMode === 'swipe'}">
          <i data-feather="layers"></i> Swipe
        </button>
        <button class="feed-view-btn ${state.feedViewMode === 'map' ? 'active' : ''}" data-view="map" onclick="switchFeedView('map')" aria-pressed="${state.feedViewMode === 'map'}">
          <i data-feather="map"></i> Map
        </button>
      </div>
    </div>
    <div id="filter-chip-strip" class="filter-chip-strip"></div>
    <div id="feed-swipe-view" style="display:${state.feedViewMode === 'swipe' ? 'contents' : 'none'}">
      <div class="feed-tabs" id="feed-tabs">
        <button class="feed-tab ${state.feedMode === 'for_you' ? 'active' : ''}" onclick="switchFeedMode('for_you')" id="tab-for-you">For You</button>
        <button class="feed-tab ${state.feedMode === 'new' ? 'active' : ''}" onclick="switchFeedMode('new')" id="tab-new">New</button>
        <button class="feed-tab ${state.feedMode === 'off_market' ? 'active' : ''}" onclick="switchFeedMode('off_market')" id="tab-off-market" title="Distressed / absentee / high-equity properties (off-MLS)">Off-Market</button>
        <div class="feed-tab-indicator" id="tab-indicator"></div>
      </div>
      <div id="markets-chip-row">${renderMarketsChipRow()}</div>
      <div id="location-bar" style="display:flex;align-items:center;justify-content:center;gap:8px;padding:4px 16px;font-size:12px;color:var(--text-secondary)">
        <i data-feather="map-pin" width="12"></i>
        <span id="location-label">Locating...</span>
      </div>
      ${true ? '' : `
        <!-- Buyer-framed CTAs deferred: "Get a buyer's agent" and
             "Complete your profile" are homebuyer concepts. Will
             resurface when we add the homebuyer layer. -->
        <div id="onboarding-progress-slot">${renderOnboardingProgressCard()}</div>
      `}
      <div class="swipe-container" id="swipe-container">
        <div class="swipe-card-wrapper">
          <div class="skeleton skeleton-card"></div>
        </div>
      </div>
      <div class="swipe-actions" id="swipe-actions" style="display:none">
        <button class="swipe-btn swipe-btn-nope" onclick="handleSwipe('left')"><i data-feather="x" width="24"></i></button>
        <button class="swipe-btn swipe-btn-info" onclick="showCurrentDetail()"><i data-feather="info" width="18"></i></button>
        <button class="swipe-btn swipe-btn-like" onclick="handleSwipe('right')"><i data-feather="heart" width="24"></i></button>
      </div>
    </div>
    <div id="feed-map-view" class="feed-map-view" style="display:${state.feedViewMode === 'map' ? 'flex' : 'none'}">
      <div id="feed-map-container" class="map-leaflet-container"></div>
      <button id="feed-map-draw-btn" class="map-draw-btn" type="button" onclick="toggleDrawMode()">
        <i data-feather="edit-3" width="14"></i> Draw
      </button>
      <button id="feed-map-locate-btn" class="map-locate-btn" type="button" aria-label="Find my location" title="Find my location" onclick="locateMe()">
        <i data-feather="navigation" width="16"></i>
      </button>
      <div id="feed-map-empty-state" class="map-empty" style="display:none">
        <i data-feather="map" width="32"></i>
        <div class="map-empty-message">Nothing to show on the map yet.</div>
      </div>
      <!-- Feature 4e: pull-up drawer. The drawer state machine and
           drag gesture live in mapView.ts (installDrawerGestures). -->
      <div id="feed-map-drawer" class="map-drawer medium" aria-label="Listings in this area">
        <div class="map-drawer-handle" id="feed-map-drawer-handle">
          <div class="map-drawer-grab"></div>
        </div>
        <div class="map-drawer-header">
          <span class="map-drawer-count" id="feed-map-drawer-count">Loading\u2026</span>
        </div>
        <div class="map-drawer-scroll" id="feed-map-drawer-scroll">
          <div class="map-drawer-loading">
            <div class="spinner"></div>
          </div>
        </div>
      </div>
      <div id="feed-map-preview-sheet" class="map-preview-sheet" hidden></div>
    </div>
  `;

  setTimeout(() => {
    initFeedWithLocation();
    updateTabIndicator();
    if (state.role === 'buyer') {
      setTimeout(() => showSwipeTutorial(), 800);
    }
    // If the user was in map mode before a re-render (localStorage
    // restore, tab switch), boot the map after initFeedWithLocation
    // has populated state.feed so the pins have something to show.
    if (state.feedViewMode === 'map') {
      setTimeout(() => { void showMap('feed'); }, 100);
    }
  }, 50);
  return div;
}


export async function initFeedWithLocation(): Promise<void> {
  if (state.feedRestoring && state.feed.length > 0) {
    state.feedRestoring = false;
    updateLocationBar();
    renderCurrentCard();
    return;
  }

  if (!state.locationRequested && state.feed.length === 0 && restoreFeedState()) {
    state.locationRequested = true;
    getUserLocation().then((loc) => {
      if (loc) {
        state.userLocation = loc;
        state.locationDenied = false;
      } else {
        state.locationDenied = true;
      }
    });
    updateLocationBar();
    renderCurrentCard();
    return;
  }

  if (!state.locationRequested) {
    state.locationRequested = true;
    updateLocationBar();
    const feedPromise = loadFeed();
    getUserLocation().then((loc) => {
      if (loc) {
        state.userLocation = loc;
        state.locationDenied = false;
      } else {
        state.locationDenied = true;
      }
      updateLocationBar();
    });
    await feedPromise;
    return;
  }
  updateLocationBar();
  await loadFeed();
}

export function switchFeedMode(mode: 'for_you' | 'new' | 'off_market'): void {
  if (state.feedMode === mode) return;

  if (state.feeds[state.feedMode]) {
    state.feeds[state.feedMode].index = state.feedIndex;
  }

  state.feedMode = mode;

  document.querySelectorAll('.feed-tab').forEach((t) => t.classList.remove('active'));
  const activeTab = document.getElementById(
    mode === 'for_you' ? 'tab-for-you' : mode === 'off_market' ? 'tab-off-market' : 'tab-new'
  );
  if (activeTab) activeTab.classList.add('active');
  updateTabIndicator();

  if (state.feeds[mode].loaded && state.feeds[mode].items.length > 0) {
    state.feed = state.feeds[mode].items;
    state.feedIndex = state.feeds[mode].index;
    renderCurrentCard();
  } else {
    const container = document.getElementById('swipe-container');
    if (container) {
      container.innerHTML = '<div class="swipe-empty"><div class="loading-spinner"></div><p style="margin-top:12px;color:var(--text-muted)">' +
        (mode === 'for_you'
          ? 'Finding homes for you...'
          : mode === 'off_market'
            ? 'Loading off-market deals...'
            : 'Loading new listings...') + '</p></div>';
    }
    loadFeedForMode(mode);
  }
}

/**
 * Feature 4e — switch between the swipe stack and the combined
 * map+drawer view. Both subtrees stay mounted; we just toggle
 * display so the swipe state (current card, gesture residue) and
 * the Leaflet instance both persist between flips.
 */
export function switchFeedView(mode: 'swipe' | 'map'): void {
  if (state.feedViewMode === mode) return;
  state.feedViewMode = mode;

  document.querySelectorAll<HTMLElement>('.feed-view-btn').forEach((btn) => {
    const isActive = btn.dataset.view === mode;
    btn.classList.toggle('active', isActive);
    btn.setAttribute('aria-pressed', String(isActive));
  });

  const swipeView = document.getElementById('feed-swipe-view');
  const mapView = document.getElementById('feed-map-view');
  if (swipeView) swipeView.style.display = mode === 'swipe' ? 'contents' : 'none';
  if (mapView) mapView.style.display = mode === 'map' ? 'flex' : 'none';

  saveFeedState();

  if (mode === 'map') {
    void showMap('feed');
  } else {
    hideAllPreviewSheets();
  }
}

export function updateTabIndicator(): void {
  const indicator = document.getElementById('tab-indicator');
  const activeTab = document.querySelector<HTMLElement>('.feed-tab.active');
  if (indicator && activeTab) {
    indicator.style.width = activeTab.offsetWidth + 'px';
    indicator.style.left = activeTab.offsetLeft + 'px';
  }
}

export function updateLocationBar(): void {
  const label = document.getElementById('location-label');
  const bar = document.getElementById('location-bar');
  if (!label || !bar) return;

  // When the active-filter chip strip is carrying the summary, hide the
  // location bar — otherwise the user sees two places showing the same
  // "N results" info with different framing.
  const hasActiveChips = countActiveFilters(state.searchFilters) > 0;
  if (hasActiveChips) {
    bar.style.display = 'none';
  } else {
    bar.style.display = '';
    if (state.searchActive && state.searchFilters.location) {
      label.textContent = `Searching: ${state.searchFilters.location} \u00b7 ${state.feed.length} results`;
    } else if (state.searchActive) {
      label.textContent = `Filtered \u00b7 ${state.feed.length} results`;
    } else if (state.userLocation) {
      label.textContent = 'Showing homes near you';
    } else {
      label.textContent = 'Showing all available homes';
    }
  }
  renderFilterChipStrip();
}

/**
 * Smart min/max → human label for beds/baths.
 *   (null, null)  → null (no chip)
 *   (2, null)     → "2+ bed"
 *   (null, 4)     → "Up to 4 bed"
 *   (2, 2)        → "2 bed" (strict)
 *   (2, 4)        → "2–4 bed" (range)
 * Pluralization: >1 upper bound uses plural noun.
 */
function rangeLabel(min: number | null, max: number | null, noun: string): string | null {
  if (min == null && max == null) return null;
  const plural = (n: number | null) => (n != null && n > 1 ? `${noun}s` : noun);
  if (min != null && max == null) return `${min}+ ${plural(min)}`;
  if (min == null && max != null) return `Up to ${max} ${plural(max)}`;
  // both set
  if (min === max) return `${min} ${plural(min)}`;
  return `${min}\u2013${max} ${plural(max)}`;
}

/**
 * Render the active-filter chip strip above the swipe feed. Each chip
 * shows a human-readable filter summary with an × to remove just that
 * filter — so the user always knows what they're looking at and can
 * loosen the search one constraint at a time.
 */
export function renderFilterChipStrip(): void {
  const container = document.getElementById('filter-chip-strip');
  if (!container) return;

  const f = state.searchFilters;
  const chips: { key: string; label: string }[] = [];
  const fmt = (n: number) => '$' + n.toLocaleString();

  if (f.minPrice != null && f.maxPrice != null) {
    chips.push({ key: 'price', label: `${fmt(f.minPrice)}\u2013${fmt(f.maxPrice)}` });
  } else if (f.minPrice != null) {
    chips.push({ key: 'price', label: `From ${fmt(f.minPrice)}` });
  } else if (f.maxPrice != null) {
    chips.push({ key: 'price', label: `Under ${fmt(f.maxPrice)}` });
  }
  // Bedrooms — smart label covering strict (min=max), range (min!=max),
  // min-only, and max-only cases. Pluralization uses "beds" for >1, etc.
  const bedLabel = rangeLabel(f.beds, f.maxBeds, 'bed');
  if (bedLabel) chips.push({ key: 'beds', label: bedLabel });
  const bathLabel = rangeLabel(f.baths, f.maxBaths, 'bath');
  if (bathLabel) chips.push({ key: 'baths', label: bathLabel });
  if (f.propertyTypes.length > 0) {
    chips.push({
      key: 'propertyTypes',
      label: f.propertyTypes.map((t) => t.replace(/_/g, ' ')).join(', '),
    });
  }
  // Consolidated HOA chip — "No HOA" if hoa=none, "HOA ≤ $X/mo" if hoaMax
  // set, otherwise nothing. Only one HOA chip ever shows in the strip.
  if (f.hoa === 'none') {
    chips.push({ key: 'hoa', label: 'No HOA' });
  } else if (f.hoaMax != null) {
    chips.push({
      key: 'hoa',
      label: f.hoaMax <= 0 ? 'No HOA only' : `HOA \u2264 ${fmt(f.hoaMax)}/mo`,
    });
  }
  if (f.minSqft != null || f.maxSqft != null) {
    const lo = f.minSqft != null ? f.minSqft.toLocaleString() : '';
    const hi = f.maxSqft != null ? f.maxSqft.toLocaleString() : '';
    chips.push({ key: 'sqft', label: `${lo || '0'}\u2013${hi || '\u221E'} sqft` });
  }
  if (f.minYearBuilt != null || f.maxYearBuilt != null) {
    const lo = f.minYearBuilt ?? '';
    const hi = f.maxYearBuilt ?? '';
    chips.push({ key: 'year', label: `Built ${lo || '\u2014'}\u2013${hi || 'now'}` });
  }
  // (HOA chip is rendered above as a single consolidated entry.)
  if (f.keywords && f.keywords.trim()) {
    chips.push({ key: 'keywords', label: `"${f.keywords.trim()}"` });
  }
  if (f.priceReduced) chips.push({ key: 'priceReduced', label: 'Price reduced' });
  if (f.hasOpenHouse) chips.push({ key: 'hasOpenHouse', label: 'Open house' });
  if (f.invMinCapRate != null) chips.push({ key: 'invMinCapRate', label: `Cap \u2265 ${f.invMinCapRate}%` });
  if (f.invMinCashOnCash != null) chips.push({ key: 'invMinCashOnCash', label: `CoC \u2265 ${f.invMinCashOnCash}%` });
  if (f.invMinDscr != null) chips.push({ key: 'invMinDscr', label: `DSCR \u2265 ${f.invMinDscr}` });
  if (f.invMinScore != null) chips.push({ key: 'invMinScore', label: `Score \u2265 ${f.invMinScore}` });

  if (chips.length === 0) {
    container.innerHTML = '';
    container.style.display = 'none';
    return;
  }

  container.style.display = '';
  const chipHtml = chips
    .map(
      (c) => `
    <button type="button" class="filter-strip-chip" data-filter-key="${c.key}" aria-label="Remove filter ${escapeHtml(c.label)}">
      <span>${escapeHtml(c.label)}</span>
      <i data-feather="x" width="12"></i>
    </button>`,
    )
    .join('');
  // Trailing result count — integrated into the chip strip so the user
  // sees "filters + how many matched" as one unit instead of a chip row
  // and a separate "Filtered · N results" line below it.
  const resultCount = state.searchActive && state.feed.length > 0
    ? `<span class="filter-strip-count">${state.feed.length} ${state.feed.length === 1 ? 'home' : 'homes'}</span>`
    : '';
  // Tier 2 — "Save this search" pill at the head of the strip when any
  // filter is active. Feature 9 persists the filter set so the user can
  // return to it from Profile → Saved Searches.
  const saveBtn = `
    <button type="button" class="filter-strip-save" onclick="saveCurrentSearch()" aria-label="Save this search">
      <i data-feather="bookmark" width="12"></i> Save
    </button>
  `;
  // Inner wrapper enables the `margin: auto` centering trick: when the
  // chips fit within the viewport the wrapper centers; when they overflow
  // it pins left and the outer scroll container takes over. Plain
  // `justify-content: center` on the outer loses centering once content
  // overflows, which is the bug swipehome#147.
  container.innerHTML = `<div class="filter-chip-strip-inner">${saveBtn}${chipHtml}${resultCount}</div>`;
  if (window.feather) window.feather.replace();
}

/**
 * Remove a single filter key from state.searchFilters and re-run the
 * search. Called from the delegated click listener on the chip strip.
 */
function removeFilterKey(key: string): void {
  const f = state.searchFilters;
  switch (key) {
    case 'price':
      f.minPrice = null;
      f.maxPrice = null;
      break;
    case 'beds':
      // The chip represents the whole bed filter — clear both bounds.
      f.beds = null;
      f.maxBeds = null;
      break;
    case 'baths':
      f.baths = null;
      f.maxBaths = null;
      break;
    case 'propertyTypes':
      f.propertyTypes = [];
      break;
    case 'hoa':
      // Consolidated HOA chip — clear both the mode and the fee cap.
      f.hoa = 'any';
      f.hoaMax = null;
      break;
    case 'sqft':
      f.minSqft = null;
      f.maxSqft = null;
      break;
    case 'year':
      f.minYearBuilt = null;
      f.maxYearBuilt = null;
      break;
    case 'keywords':
      f.keywords = '';
      break;
    case 'priceReduced':
      f.priceReduced = false;
      break;
    case 'hasOpenHouse':
      f.hasOpenHouse = false;
      break;
    case 'invMinCapRate':
      f.invMinCapRate = null;
      break;
    case 'invMinCashOnCash':
      f.invMinCashOnCash = null;
      break;
    case 'invMinDscr':
      f.invMinDscr = null;
      break;
    case 'invMinScore':
      f.invMinScore = null;
      break;
  }
  saveFeedState();
  if (countActiveFilters(f) === 0 && !f.location) {
    clearSearch();
    return;
  }
  runSearch();
}

// Delegated click handler on the chip strip — installed once when the
// feed screen mounts. Using event delegation so this listener survives
// innerHTML re-renders of the strip.
document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement | null;
  const chip = target?.closest<HTMLElement>('.filter-strip-chip');
  if (!chip) return;
  const key = chip.dataset.filterKey;
  if (key) {
    e.preventDefault();
    removeFilterKey(key);
  }
});

export function buildFeedParams(): string {
  const params = new URLSearchParams();
  params.append('limit', '30');
  if (state.userLocation) {
    params.append('lat', String(state.userLocation.lat));
    params.append('lng', String(state.userLocation.lng));
    if (state.feedRadius > 0) params.append('radius', String(state.feedRadius));
  }
  return params.toString();
}

/**
 * Remember `financing.dscr_applicable` from the last /investment-feed
 * response. A cash profile has no debt, so the backend reports
 * dscr_applicable=false and ignores `min_dscr`; the filter drawer reads
 * this flag to disable its DSCR slider and say why. A response without
 * the block (older backend) leaves the slider active.
 */
function rememberFeedFinancing(data: InvestmentFeedResponse | null | undefined): void {
  const flag = data?.financing?.dscr_applicable;
  state.feedDscrApplicable = typeof flag === 'boolean' ? flag : true;
}

/**
 * Build the query string for /api/listings/investment-feed.
 *
 * Only explicit user-applied filters (from the drawer) are passed as
 * hard filters. The user's saved investment_criteria are treated as
 * default values the filter sliders initialize to — not as a hidden
 * pre-filter on the feed. Otherwise a brand-new investor whose
 * onboarding answers don't match current inventory sees an empty
 * feed, which reads as "this app is broken" instead of "dial down
 * your criteria."
 */

/**
 * Wishlist #2: render the multi-market chip row above the feed tabs.
 * Reads target_markets from the user's investment_criteria. If the user
 * has no markets set (e.g. legacy buyer accounts), the row stays empty
 * — the search bar still works as the fallback market filter. Each chip
 * toggles in/out of state.activeMarkets and reloads the feed.
 */
function renderMarketsChipRow(): string {
  const criteria = (state.user?.investment_criteria as { target_markets?: string[] } | undefined) || {};
  const markets = criteria.target_markets || [];
  if (markets.length === 0) return '';

  const active = state.activeMarkets || [];
  const chips = markets.map((m) => {
    const isActive = active.includes(m);
    return `
      <button
        type="button"
        class="markets-chip ${isActive ? 'active' : ''}"
        onclick="toggleMarketChip('${escapeHtml(m)}')"
        style="padding:5px 12px;border-radius:14px;font-size:12px;font-weight:600;cursor:pointer;border:1px solid ${isActive ? 'var(--primary)' : 'var(--border)'};background:${isActive ? 'var(--primary)' : 'transparent'};color:${isActive ? '#fff' : 'var(--text-secondary)'};white-space:nowrap"
      >${escapeHtml(m)}</button>
    `;
  }).join('');

  // "All markets" pseudo-chip clears the active selection.
  const allActive = active.length === 0;
  return `
    <div style="display:flex;gap:6px;padding:6px 16px 0;overflow-x:auto;-webkit-overflow-scrolling:touch">
      <button
        type="button"
        class="markets-chip ${allActive ? 'active' : ''}"
        onclick="toggleMarketChip('__all__')"
        style="padding:5px 12px;border-radius:14px;font-size:12px;font-weight:600;cursor:pointer;border:1px solid ${allActive ? 'var(--primary)' : 'var(--border)'};background:${allActive ? 'var(--primary)' : 'transparent'};color:${allActive ? '#fff' : 'var(--text-secondary)'};white-space:nowrap"
      >All markets</button>
      ${chips}
    </div>
  `;
}

/**
 * Wishlist #2: toggle a market in/out of the active selection and reload
 * the feed. Special value '__all__' clears the selection (returns to
 * showing every market in the user's investment_criteria).
 */
export function toggleMarketChip(market: string): void {
  if (market === '__all__') {
    state.activeMarkets = [];
  } else {
    const active = state.activeMarkets || [];
    if (active.includes(market)) {
      state.activeMarkets = active.filter((m) => m !== market);
    } else {
      state.activeMarkets = [...active, market];
    }
  }
  // Re-render the chip row to reflect the new active state, then reload
  // the feed with the new market filter.
  const row = document.getElementById('markets-chip-row');
  if (row) row.innerHTML = renderMarketsChipRow();
  void loadFeedForMode(state.feedMode);
}

export function buildInvestorFeedParams(): string {
  const p = new URLSearchParams();
  p.append('limit', '30');
  const f = state.searchFilters;

  const addIf = (key: string, val: number | null | undefined) => {
    if (val == null) return;
    p.append(key, String(val));
  };

  // Path B: when the user is on the Off-Market tab, flip the feed into
  // off-market mode. The backend swaps base status active -> draft and
  // restricts to rows with data_source set, so we only see real
  // off-market provenance (no stray drafts). The investment_score
  // soft floor is intentionally NOT applied in off-market mode —
  // investors evaluate distressed properties on distress signal +
  // equity, not on score.
  const isOffMarket = state.feedMode === 'off_market';
  if (isOffMarket) {
    p.append('off_market', 'true');
  }

  // Strict filters — only sent when the user explicitly dialed the
  // corresponding slider in the drawer (state.searchFilters.*).
  addIf('min_cap_rate', f.invMinCapRate);
  addIf('min_coc', f.invMinCashOnCash);
  addIf('min_dscr', f.invMinDscr);
  addIf('min_price', f.minPrice);
  addIf('max_price', f.maxPrice);

  // Score floor: only when the user explicitly sets a min_score. A
  // previous soft default (min_score=50) hid most listings on first load,
  // leaving an empty "No deals cleared your floor" feed; it has been
  // removed so listings show immediately.
  if (f.invMinScore != null) {
    addIf('min_score', f.invMinScore);
  }
  state.feedAppliedDefaultFloor = false;

  // Location precedence (wishlist #2):
  //   1. Multi-market chip selection on Discover (state.activeMarkets) wins
  //      — sent as comma-separated `markets` so the backend unions them.
  //   2. Explicit single-market filter text (search bar) falls through as
  //      the legacy `market` param.
  const activeMarkets = state.activeMarkets || [];
  if (activeMarkets.length > 0) {
    p.append('markets', activeMarkets.join(','));
  } else if (f.location) {
    p.append('market', f.location);
  }

  // Property type: only from explicit selection.
  const ptype = f.propertyTypes[0];
  if (ptype) p.append('property_type', ptype);

  // Investor-POV fix #2: forward sort to the backend so the user can
  // pivot between investment score, cap rate, CoC, DSCR, days listed,
  // and the legacy buyer sorts (price low/high, newest). The mapping
  // lives in the backend route (investment.py /investment-feed).
  const sortMap: Record<string, string> = {
    score: 'score',
    cap_rate: 'cap_rate',
    cash_on_cash: 'cash_on_cash',
    dscr: 'dscr',
    days_listed: 'days_listed',
    price_low: 'price_low',
    price_high: 'price_high',
    newest: 'newest',
  };
  const sortKey = sortMap[f.sort] || 'score';
  p.append('sort', sortKey);

  return p.toString();
}

export async function loadFeedForMode(mode: 'for_you' | 'new' | 'off_market'): Promise<void> {
  void mode; // both modes use the investment feed now
  try {
    let listings: FeedListing[];

    // Investor-first: always use the investment feed.
    {
      const investorQs = buildInvestorFeedParams();
      const data = await api<InvestmentFeedResponse>('GET', `/listings/investment-feed?${investorQs}`);
      listings = data.items || [];
      rememberFeedFinancing(data);
    }
    state.feeds[mode].items = listings;
    state.feeds[mode].index = 0;
    state.feeds[mode].loaded = true;
    state.feed = listings;
    state.feedIndex = 0;
    saveFeedState();
    renderCurrentCard();
    onMapFeedRefreshed();
  } catch (err) {
    const container = document.getElementById('swipe-container');
    if (container) {
      const message = err instanceof Error ? err.message : 'Failed to load feed';
      container.innerHTML = `<div class="swipe-empty"><i data-feather="alert-circle" width="48"></i><h3>Couldn't load feed</h3><p>${escapeHtml(message)}</p><button class="btn btn-secondary mt-16" onclick="loadFeedForMode('${mode}')">Retry</button></div>`;
      if (window.feather) window.feather.replace();
    }
  }
}

export async function loadFeed(): Promise<void> {
  if (state.searchActive) {
    await runSearch();
    return;
  }
  await loadFeedForMode(state.feedMode);
}

export async function refreshFeed(): Promise<void> {
  if (state.searchActive) {
    await runSearch();
  } else {
    state.feed = [];
    state.feedIndex = 0;
    state.feeds[state.feedMode].loaded = false;
    state.feeds[state.feedMode].items = [];
    state.feeds[state.feedMode].index = 0;
    await loadFeed();
  }
}

// ─── Search & Filters ─────────────────────────────────
interface SearchBody {
  location: string | null;
  lat: number | null;
  lng: number | null;
  radius: number;
  min_price: number | null;
  max_price: number | null;
  min_beds: number | null;
  max_beds: number | null;
  min_baths: number | null;
  max_baths: number | null;
  property_types: string | null;
  hoa: string;
  sort: string;
  // Feature 3 advanced filters — snake_case for the wire
  min_sqft: number | null;
  max_sqft: number | null;
  min_year_built: number | null;
  max_year_built: number | null;
  hoa_max: number | null;
  keywords: string | null;
  price_reduced: boolean | null;
  has_open_house: boolean | null;
}

/**
 * Build a /listings/search request body from the current filter state.
 * Shared between runSearch() and the count-preview debounce so the two
 * can never drift.
 */
function buildSearchBody(location: string): SearchBody {
  const f = state.searchFilters;
  return {
    location: location || null,
    lat: !location && state.userLocation ? state.userLocation.lat : null,
    lng: !location && state.userLocation ? state.userLocation.lng : null,
    radius: f.radius,
    min_price: f.minPrice || null,
    max_price: f.maxPrice || null,
    min_beds: f.beds || null,
    max_beds: f.maxBeds || null,
    min_baths: f.baths || null,
    max_baths: f.maxBaths || null,
    property_types: f.propertyTypes.length ? f.propertyTypes.join(',') : null,
    hoa: f.hoa,
    sort: f.sort,
    min_sqft: f.minSqft,
    max_sqft: f.maxSqft,
    min_year_built: f.minYearBuilt,
    max_year_built: f.maxYearBuilt,
    hoa_max: f.hoaMax,
    keywords: f.keywords && f.keywords.trim() ? f.keywords.trim() : null,
    price_reduced: f.priceReduced ? true : null,
    has_open_house: f.hasOpenHouse ? true : null,
  };
}

/**
 * Count how many of the advanced filter fields are currently active.
 * Powers the filter badge in the header and the chip strip above the feed.
 */
export function countActiveFilters(f: SearchFilters): number {
  let n = 0;
  if (f.minPrice != null) n++;
  if (f.maxPrice != null) n++;
  if (f.beds != null || f.maxBeds != null) n++;
  if (f.baths != null || f.maxBaths != null) n++;
  if (f.propertyTypes.length > 0) n++;
  if (f.hoa !== 'any') n++;
  if (f.minSqft != null) n++;
  if (f.maxSqft != null) n++;
  if (f.minYearBuilt != null) n++;
  if (f.maxYearBuilt != null) n++;
  if (f.hoaMax != null) n++;
  if (f.keywords && f.keywords.trim()) n++;
  if (f.priceReduced) n++;
  if (f.hasOpenHouse) n++;
  if (f.invMinCapRate != null) n++;
  if (f.invMinCashOnCash != null) n++;
  if (f.invMinDscr != null) n++;
  if (f.invMinScore != null) n++;
  return n;
}

export async function runSearch(): Promise<void> {
  const locationInput = document.getElementById('search-location') as HTMLInputElement | null;
  const location = locationInput ? locationInput.value.trim() : state.searchFilters.location;
  state.searchFilters.location = location;

  const container = document.getElementById('swipe-container');
  if (container) {
    container.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;height:100%;gap:8px;color:var(--text-secondary)"><div class="spinner"></div><span>Searching...</span></div>';
  }

  // Investor-first: always use investment feed for search.

  try {
    let listings: FeedListing[];
    {
      const investorQs = buildInvestorFeedParams();
      const data = await api<InvestmentFeedResponse>('GET', `/listings/investment-feed?${investorQs}`);
      listings = data.items || [];
      rememberFeedFinancing(data);
    }
    state.feed = listings;
    state.feedIndex = 0;
    state.searchActive = true;
    saveFeedState();
    renderCurrentCard();
    updateLocationBar();
    onMapFeedRefreshed();
  } catch (err) {
    if (container) {
      const message = err instanceof Error ? err.message : 'Search failed';
      container.innerHTML = `<div class="swipe-empty"><i data-feather="alert-circle" width="48"></i><h3>Search failed</h3><p>${escapeHtml(message)}</p><button class="btn btn-secondary mt-16" onclick="runSearch()">Retry</button></div>`;
      if (window.feather) window.feather.replace();
    }
  }
}

/** Return a fresh SearchFilters object at default values. */
function defaultFilters(): SearchFilters {
  return {
    location: '',
    minPrice: null,
    maxPrice: null,
    beds: null,
    maxBeds: null,
    baths: null,
    maxBaths: null,
    propertyTypes: [],
    hoa: 'any',
    // Investor-POV fix #2: default to investment score so Reset Filters
    // returns to the investor-first sort instead of the legacy buyer
    // "newest" default.
    sort: 'score',
    radius: 25,
    minSqft: null,
    maxSqft: null,
    minYearBuilt: null,
    maxYearBuilt: null,
    hoaMax: null,
    keywords: '',
    priceReduced: false,
    hasOpenHouse: false,
    invMinCapRate: null,
    invMinCashOnCash: null,
    invMinDscr: null,
    invMinScore: null,
  };
}

export function clearSearch(): void {
  state.searchFilters = defaultFilters();
  state.searchActive = false;
  state.feed = [];
  state.feedIndex = 0;
  clearFeedState();
  navigate('feed');
}

/**
 * Softer clear: drops every advanced filter but keeps the location
 * the user typed and re-runs the search. Wired to the "Clear Filters"
 * button on the empty state when a search returned zero results.
 */
export function clearFiltersOnly(): void {
  const keptLocation = state.searchFilters.location;
  state.searchFilters = { ...defaultFilters(), location: keptLocation };
  saveFeedState();
  if (keptLocation) {
    runSearch();
  } else {
    clearSearch();
  }
}

/**
 * Legacy no-op kept for compatibility: the old soft score floor
 * (min_score=50) has been removed, so there is nothing to dismiss.
 * Reloads the feed to reflect current filters.
 */
export function showWeakerDeals(): void {
  state.showWeakDeals = true;
  state.feedAppliedDefaultFloor = false;
  loadFeedForMode(state.feedMode);
}

export function openFilterDrawer(): void {
  const modal = document.getElementById('modal-container');
  if (!modal) return;
  modal.classList.remove('hidden');
  document.body.style.overflow = 'hidden';

  const f = state.searchFilters;
  // financing.dscr_applicable from the last feed response (feedDscrApplicable):
  // false = cash profile, the backend ignores min_dscr — disable the slider.
  const dscrApplicable = state.feedDscrApplicable !== false;
  const bedOptions = [1, 2, 3, 4, 5];
  const bathOptions = [1, 2, 3, 4];
  const typeOptions: { value: PropertyType; label: string }[] = [
    { value: 'single_family', label: 'Single Family' },
    { value: 'condo', label: 'Condo' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'multi_family', label: 'Multi-Family' },
  ];
  // Investor-POV fix #2: investor sorts lead, buyer sorts follow. The
  // Discover feed routes through the /investment-feed endpoint
  // (post Step 2 convergence) which honors all these via the sort param
  // forwarded by buildInvestorFeedParams.
  const sortOptions = [
    { value: 'score', label: 'Investment score' },
    { value: 'cap_rate', label: 'Cap rate' },
    { value: 'cash_on_cash', label: 'Cash-on-cash' },
    { value: 'dscr', label: 'DSCR' },
    { value: 'days_listed', label: 'Days listed' },
    { value: 'newest', label: 'Newest' },
    { value: 'price_low', label: 'Price: Low \u2192 High' },
    { value: 'price_high', label: 'Price: High \u2192 Low' },
  ];
  const radiusOptions = [5, 10, 25, 50];

  // Determine which HOA mode chip should start active based on state
  const hoaMode: 'any' | 'none' | 'max' =
    f.hoa === 'none' ? 'none' : f.hoaMax != null ? 'max' : 'any';

  // Render one Min/Max chip row pair for beds or baths. Sharing the
  // markup keeps the two bedroom/bathroom sections consistent.
  const renderMinMaxChipRow = (
    label: string,
    minKey: 'beds' | 'baths',
    maxKey: 'maxBeds' | 'maxBaths',
    options: number[],
    plusAt: number,
  ): string => {
    const minVal = f[minKey];
    const maxVal = f[maxKey];
    const chipFor = (key: 'beds' | 'baths' | 'maxBeds' | 'maxBaths', val: number | null, current: number | null) =>
      `<button class="filter-chip ${current === val ? 'active' : ''}" onclick="setFilterChip('${key}', ${val === null ? 'null' : val}, this)">${val === null ? 'Any' : val + (val === plusAt ? '+' : '')}</button>`;
    return `
      <div class="filter-section">
        <div class="filter-label">${label}</div>
        <div class="filter-minmax-sub">Min</div>
        <div class="filter-chip-row">
          ${chipFor(minKey, null, minVal)}
          ${options.map((n) => chipFor(minKey, n, minVal)).join('')}
        </div>
        <div class="filter-minmax-sub">Max</div>
        <div class="filter-chip-row">
          ${chipFor(maxKey, null, maxVal)}
          ${options.map((n) => chipFor(maxKey, n, maxVal)).join('')}
        </div>
      </div>
    `;
  };

  modal.innerHTML = `
    <div class="modal-overlay" onclick="closeModal()"></div>
    <div class="modal-sheet filter-drawer">
      <div class="modal-handle"></div>
      <div class="modal-header">
        <h2>Filters</h2>
        <button class="modal-close" onclick="closeModal()"><i data-feather="x" width="20"></i></button>
      </div>
      <div style="padding:16px 20px 100px;overflow-y:auto">

        <!-- Sort — its own category at the top, visually separated from the filters below -->
        <div class="filter-section filter-section-sort">
          <div class="filter-group-header">Sort</div>
          <div class="filter-chip-row" style="flex-wrap:wrap">
            ${sortOptions.map((s) => `<button class="filter-chip ${f.sort === s.value ? 'active' : ''}" onclick="setFilterChip('sort', '${s.value}', this)">${s.label}</button>`).join('')}
          </div>
        </div>

        <!-- Investor-POV fix #2: investment filters move to the top of the
             Filters section because they're the primary thing an investor
             tunes. Beds / baths / sqft / etc. follow below as secondary
             property filters. -->
        <div class="filter-group-header">Investment filters</div>
        <div class="filter-section">
          <div class="inv-filter-row">
            <label class="inv-filter-label">Min cap rate
              <span id="inv-filter-cap-display">${f.invMinCapRate != null ? f.invMinCapRate.toFixed(1) + '%' : 'Any'}</span>
            </label>
            <input type="range" id="inv-filter-cap" min="0" max="12" step="0.5"
                   value="${f.invMinCapRate ?? 0}" oninput="onInvFilterSlider()">
          </div>
          <div class="inv-filter-row">
            <label class="inv-filter-label">Min cash-on-cash
              <span id="inv-filter-coc-display">${f.invMinCashOnCash != null ? f.invMinCashOnCash.toFixed(1) + '%' : 'Any'}</span>
            </label>
            <input type="range" id="inv-filter-coc" min="0" max="20" step="0.5"
                   value="${f.invMinCashOnCash ?? 0}" oninput="onInvFilterSlider()">
          </div>
          <div class="inv-filter-row${dscrApplicable ? '' : ' inv-filter-row-disabled'}">
            <label class="inv-filter-label">Min DSCR
              <span id="inv-filter-dscr-display">${dscrApplicable ? (f.invMinDscr != null ? f.invMinDscr.toFixed(2) : 'Any') : 'Not applied'}</span>
            </label>
            <input type="range" id="inv-filter-dscr" min="0" max="2" step="0.05"
                   value="${f.invMinDscr ?? 0}" oninput="onInvFilterSlider()"${dscrApplicable ? '' : ' disabled aria-disabled="true"'}>
            ${dscrApplicable ? '' : `<div class="inv-filter-note" id="inv-filter-dscr-note">Cash profile \u2014 no debt, DSCR filter not applied</div>`}
          </div>
          <div class="inv-filter-row">
            <label class="inv-filter-label">Min deal score
              <span id="inv-filter-score-display">${f.invMinScore != null ? String(f.invMinScore) : 'Any'}</span>
            </label>
            <input type="range" id="inv-filter-score" min="0" max="100" step="5"
                   value="${f.invMinScore ?? 0}" oninput="onInvFilterSlider()">
          </div>
        </div>

        <div class="filter-group-header">Property filters</div>

        <div class="filter-section">
          <div class="filter-label">Price Range</div>
          <div class="filter-price-row">
            <div class="filter-price-input">
              <span class="filter-price-prefix">$</span>
              <input type="number" class="input" id="filter-min-price" placeholder="Min" value="${f.minPrice || ''}" style="padding-left:24px">
            </div>
            <span style="color:var(--text-muted)">\u2014</span>
            <div class="filter-price-input">
              <span class="filter-price-prefix">$</span>
              <input type="number" class="input" id="filter-max-price" placeholder="Max" value="${f.maxPrice || ''}" style="padding-left:24px">
            </div>
          </div>
        </div>

        ${renderMinMaxChipRow('Bedrooms', 'beds', 'maxBeds', bedOptions, 5)}
        ${renderMinMaxChipRow('Bathrooms', 'baths', 'maxBaths', bathOptions, 4)}

        <div class="filter-section">
          <div class="filter-label">Square Footage</div>
          <div class="filter-price-row">
            <div class="filter-price-input">
              <input type="number" inputmode="numeric" class="input" id="filter-min-sqft" placeholder="Min sqft" value="${f.minSqft ?? ''}">
            </div>
            <span style="color:var(--text-muted)">\u2014</span>
            <div class="filter-price-input">
              <input type="number" inputmode="numeric" class="input" id="filter-max-sqft" placeholder="Max sqft" value="${f.maxSqft ?? ''}">
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-label">Year Built</div>
          <div class="filter-price-row">
            <div class="filter-price-input">
              <input type="number" inputmode="numeric" class="input" id="filter-min-year" placeholder="From" value="${f.minYearBuilt ?? ''}">
            </div>
            <span style="color:var(--text-muted)">\u2014</span>
            <div class="filter-price-input">
              <input type="number" inputmode="numeric" class="input" id="filter-max-year" placeholder="To" value="${f.maxYearBuilt ?? ''}">
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-label">Property Type</div>
          <div class="filter-chip-row" style="flex-wrap:wrap">
            ${typeOptions.map((t) => `<button class="filter-chip filter-chip-toggle ${f.propertyTypes.includes(t.value) ? 'active' : ''}" onclick="toggleFilterType('${t.value}', this)">${t.label}</button>`).join('')}
          </div>
        </div>

        <!-- HOA — consolidated three-way selector.
             "Max fee" reveals the inline input below; Any/No HOA hide it. -->
        <div class="filter-section">
          <div class="filter-label">HOA</div>
          <div class="filter-chip-row">
            <button class="filter-chip ${hoaMode === 'any' ? 'active' : ''}" onclick="setFilterChip('hoaMode', 'any', this)">Any</button>
            <button class="filter-chip ${hoaMode === 'none' ? 'active' : ''}" onclick="setFilterChip('hoaMode', 'none', this)">No HOA</button>
            <button class="filter-chip ${hoaMode === 'max' ? 'active' : ''}" onclick="setFilterChip('hoaMode', 'max', this)">Max fee</button>
          </div>
          <div id="filter-hoa-max-wrap" class="filter-hoa-max-wrap" style="display:${hoaMode === 'max' ? '' : 'none'}">
            <div class="filter-price-row">
              <div class="filter-price-input">
                <span class="filter-price-prefix">$</span>
                <input type="number" inputmode="numeric" class="input" id="filter-hoa-max" placeholder="500" value="${f.hoaMax ?? ''}" style="padding-left:24px">
              </div>
              <span style="color:var(--text-muted);font-size:12px">/ month</span>
            </div>
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-label">Radius</div>
          <div class="filter-chip-row">
            ${radiusOptions.map((r) => `<button class="filter-chip ${f.radius === r ? 'active' : ''}" onclick="setFilterChip('radius', ${r}, this)">${r} mi</button>`).join('')}
          </div>
        </div>

        <div class="filter-section">
          <div class="filter-label">Keywords</div>
          <input type="text" class="input" id="filter-keywords" placeholder="e.g. renovated, waterfront, cul-de-sac" value="${escapeHtml(f.keywords || '')}">
          <div style="font-size:11px;color:var(--text-muted);margin-top:6px">Searches each listing's description, address, and brokerage.</div>
        </div>

        <div class="filter-section">
          <div class="toggle-row">
            <div>
              <div class="toggle-label">Price reduced</div>
              <div class="toggle-sublabel">Only homes with a verified price drop</div>
            </div>
            <label class="toggle">
              <input type="checkbox" id="filter-price-reduced" ${f.priceReduced ? 'checked' : ''}>
              <span class="toggle-slider"></span>
            </label>
          </div>
          <div class="toggle-row">
            <div>
              <div class="toggle-label">Open houses</div>
              <div class="toggle-sublabel">Homes with a scheduled open house coming up</div>
            </div>
            <label class="toggle">
              <input type="checkbox" id="filter-open-house" ${f.hasOpenHouse ? 'checked' : ''}>
              <span class="toggle-slider"></span>
            </label>
          </div>
        </div>

      </div>

      <div class="filter-actions">
        <button class="btn btn-secondary" onclick="resetFilters()" style="flex:0.4">Reset</button>
        <button class="btn btn-primary" id="filter-apply-btn" onclick="applyFilters()" style="flex:0.6">Apply Filters</button>
      </div>
    </div>
  `;

  if (window.feather) window.feather.replace();

  // Wire live "Show N homes" count preview — debounced so we don't hammer
  // the API on every keystroke. Uses addEventListener (never clobbers).
  attachCountPreviewListeners();
  schedulePreviewCount();
}

type FilterChipKey =
  | 'beds'
  | 'maxBeds'
  | 'baths'
  | 'maxBaths'
  | 'hoa'
  | 'hoaMode'
  | 'sort'
  | 'radius';
type FilterChipValue = number | string | null;

export function setFilterChip(key: FilterChipKey, value: FilterChipValue, btn: HTMLElement): void {
  const f = state.searchFilters;
  if (key === 'beds') f.beds = value as number | null;
  else if (key === 'maxBeds') f.maxBeds = value as number | null;
  else if (key === 'baths') f.baths = value as number | null;
  else if (key === 'maxBaths') f.maxBaths = value as number | null;
  else if (key === 'hoa') f.hoa = value as 'any' | 'none';
  else if (key === 'hoaMode') {
    // Three-way consolidated HOA selector:
    //   'any'  → any HOA, no fee cap
    //   'none' → only listings without an HOA
    //   'max'  → listings with no HOA OR with a fee ≤ hoaMax input
    const mode = value as 'any' | 'none' | 'max';
    const maxWrap = document.getElementById('filter-hoa-max-wrap');
    if (mode === 'any') {
      f.hoa = 'any';
      f.hoaMax = null;
      if (maxWrap) maxWrap.style.display = 'none';
    } else if (mode === 'none') {
      f.hoa = 'none';
      f.hoaMax = null;
      if (maxWrap) maxWrap.style.display = 'none';
    } else {
      // 'max' — keep HOA chip on 'any' (we're filtering by fee, not presence),
      // prefill 500 if not yet set so the filter actually applies, and reveal
      // the input so the user can tweak it.
      f.hoa = 'any';
      if (f.hoaMax == null) f.hoaMax = 500;
      if (maxWrap) {
        maxWrap.style.display = '';
        const input = document.getElementById('filter-hoa-max') as HTMLInputElement | null;
        if (input) {
          if (!input.value) input.value = String(f.hoaMax);
          setTimeout(() => input.focus(), 0);
        }
      }
    }
  }
  else if (key === 'sort') f.sort = value as SearchFilters['sort'];
  else if (key === 'radius') f.radius = value as number;

  const row = btn.parentElement;
  row?.querySelectorAll<HTMLElement>('.filter-chip:not(.filter-chip-toggle)').forEach((c) => c.classList.remove('active'));
  btn.classList.add('active');
}

export function toggleFilterType(type: PropertyType, btn: HTMLElement): void {
  const types = state.searchFilters.propertyTypes;
  const idx = types.indexOf(type);
  if (idx >= 0) {
    types.splice(idx, 1);
    btn.classList.remove('active');
  } else {
    types.push(type);
    btn.classList.add('active');
  }
}

export function resetFilters(): void {
  // Preserve the location field — resetting the drawer should not wipe
  // the user's city. Only advanced filters get cleared.
  const keptLocation = state.searchFilters.location;
  state.searchFilters = { ...defaultFilters(), location: keptLocation };
  saveFeedState();
  // Re-render drawer with defaults
  const modal = document.getElementById('modal-container');
  if (modal) {
    modal.classList.add('hidden');
    modal.innerHTML = '';
    document.body.style.overflow = '';
  }
  openFilterDrawer();
}

/**
 * Read every input in the drawer back into state.searchFilters.
 * Shared between applyFilters() and the live count-preview debounce so
 * both see exactly the same filter snapshot.
 */
function syncFiltersFromDrawer(): void {
  const num = (id: string): number | null => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el || !el.value.trim()) return null;
    const parsed = parseInt(el.value, 10);
    return Number.isFinite(parsed) ? parsed : null;
  };
  const text = (id: string): string => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    return el ? el.value.trim() : '';
  };
  const checked = (id: string): boolean => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    return !!(el && el.checked);
  };

  const f = state.searchFilters;
  f.minPrice = num('filter-min-price');
  f.maxPrice = num('filter-max-price');
  f.minSqft = num('filter-min-sqft');
  f.maxSqft = num('filter-max-sqft');
  f.minYearBuilt = num('filter-min-year');
  f.maxYearBuilt = num('filter-max-year');
  f.hoaMax = num('filter-hoa-max');
  f.keywords = text('filter-keywords');
  f.priceReduced = checked('filter-price-reduced');
  f.hasOpenHouse = checked('filter-open-house');

  // Investment sliders — always present in the filter drawer.
  // A value of 0 is treated as "Any" (filter not applied) so clearing the
  // slider to the left end removes the filter.
  const invNum = (id: string): number | null => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return state.searchFilters[id === 'inv-filter-cap' ? 'invMinCapRate'
      : id === 'inv-filter-coc' ? 'invMinCashOnCash'
      : id === 'inv-filter-dscr' ? 'invMinDscr' : 'invMinScore'];
    const v = Number(el.value);
    return v > 0 ? v : null;
  };
  if (document.getElementById('inv-filter-cap')) {
    f.invMinCapRate = invNum('inv-filter-cap');
    f.invMinCashOnCash = invNum('inv-filter-coc');
    f.invMinDscr = invNum('inv-filter-dscr');
    f.invMinScore = invNum('inv-filter-score');
  }
}

// ─── Investor filter slider live display ──────────────────────────────
// Called from the drawer's range input oninput — no state commit, just
// the readout update. applyFilters() commits on "Apply Filters" press.
export function onInvFilterSlider(): void {
  const upd = (id: string, displayId: string, suffix: string, decimals: number) => {
    const el = document.getElementById(id) as HTMLInputElement | null;
    const disp = document.getElementById(displayId);
    if (!el || !disp) return;
    const v = Number(el.value);
    disp.textContent = v > 0 ? v.toFixed(decimals) + suffix : 'Any';
  };
  upd('inv-filter-cap', 'inv-filter-cap-display', '%', 1);
  upd('inv-filter-coc', 'inv-filter-coc-display', '%', 1);
  if (state.feedDscrApplicable !== false) upd('inv-filter-dscr', 'inv-filter-dscr-display', '', 2);
  upd('inv-filter-score', 'inv-filter-score-display', '', 0);
}

export function applyFilters(): void {
  syncFiltersFromDrawer();

  // Soft validation: min > max on price, sqft, or year is a user error.
  // Surface it inline rather than silently returning zero results.
  const f = state.searchFilters;
  const applyBtn = document.getElementById('filter-apply-btn') as HTMLButtonElement | null;
  const invalid: string[] = [];
  if (f.minPrice != null && f.maxPrice != null && f.minPrice > f.maxPrice) invalid.push('price');
  if (f.minSqft != null && f.maxSqft != null && f.minSqft > f.maxSqft) invalid.push('square footage');
  if (f.minYearBuilt != null && f.maxYearBuilt != null && f.minYearBuilt > f.maxYearBuilt) invalid.push('year built');
  if (invalid.length > 0) {
    if (applyBtn) {
      applyBtn.textContent = `Min ${invalid[0]} is above max`;
      applyBtn.disabled = true;
      setTimeout(() => {
        if (applyBtn) {
          applyBtn.disabled = false;
          applyBtn.textContent = 'Apply Filters';
        }
      }, 2500);
    }
    return;
  }

  const modal = document.getElementById('modal-container');
  if (modal) {
    modal.classList.add('hidden');
    modal.innerHTML = '';
    document.body.style.overflow = '';
  }
  cancelPreviewCount();
  state.searchActive = true;
  saveFeedState();
  runSearch();
}

// ─── Count-preview (live "Show N homes" on the Apply button) ──────────
let previewCountTimer: number | null = null;
let previewCountSeq = 0;

function cancelPreviewCount(): void {
  if (previewCountTimer != null) {
    clearTimeout(previewCountTimer);
    previewCountTimer = null;
  }
}

function schedulePreviewCount(): void {
  cancelPreviewCount();
  const mySeq = ++previewCountSeq;
  previewCountTimer = window.setTimeout(async () => {
    // If the drawer was closed between scheduling and firing, bail.
    if (!document.getElementById('filter-apply-btn')) return;
    syncFiltersFromDrawer();

    // Don't issue a count call if there are no filters at all — the
    // button stays as "Apply Filters" and we avoid hammering RapidAPI.
    if (countActiveFilters(state.searchFilters) === 0) {
      const btn = document.getElementById('filter-apply-btn') as HTMLButtonElement | null;
      if (btn) btn.textContent = 'Apply Filters';
      return;
    }

    const locationInput = document.getElementById('search-location') as HTMLInputElement | null;
    const location = locationInput ? locationInput.value.trim() : state.searchFilters.location;
    const body = buildSearchBody(location);
    try {
      const data = await api<{ count?: number }>('POST', '/listings/search/count', body);
      // Stale-response guard: ignore if a newer request has started.
      if (mySeq !== previewCountSeq) return;
      const btn = document.getElementById('filter-apply-btn') as HTMLButtonElement | null;
      if (!btn) return;
      const count = data.count ?? 0;
      if (count === 0) {
        btn.textContent = 'No matches — adjust filters';
        btn.classList.add('filter-apply-empty');
      } else {
        btn.textContent = `Show ${count.toLocaleString()} home${count === 1 ? '' : 's'}`;
        btn.classList.remove('filter-apply-empty');
      }
    } catch {
      // Silent fall-through to the plain label so a flaky network
      // never blocks the user from applying filters.
      const btn = document.getElementById('filter-apply-btn') as HTMLButtonElement | null;
      if (btn && mySeq === previewCountSeq) btn.textContent = 'Apply Filters';
    }
  }, 400);
}

function attachCountPreviewListeners(): void {
  const drawer = document.querySelector('.filter-drawer');
  if (!drawer) return;
  // Inputs → debounce on every keystroke. Chips (.filter-chip) already
  // call setFilterChip which mutates state directly; we piggyback via a
  // delegated click listener so they trigger the preview too.
  drawer.querySelectorAll<HTMLInputElement>('input').forEach((input) => {
    input.addEventListener('input', schedulePreviewCount);
    input.addEventListener('change', schedulePreviewCount);
  });
  drawer.addEventListener('click', (e) => {
    const target = e.target as HTMLElement | null;
    if (target && target.closest('.filter-chip')) {
      // setFilterChip already ran synchronously via the onclick attr,
      // so state.searchFilters is fresh by the time our debounce fires.
      schedulePreviewCount();
    }
  });
}

export function renderCurrentCard(): void {
  const container = document.getElementById('swipe-container');
  const actions = document.getElementById('swipe-actions');
  if (!container) return;

  if (state.feedIndex >= state.feed.length) {
    // When a filtered search returns zero results, show a dedicated
    // empty state that explains WHY the feed is empty and gives the
    // user a one-tap path out — rather than the generic "You've seen
    // all your matches" which reads as a success message.
    const filteredEmpty =
      state.searchActive && state.feed.length === 0 && countActiveFilters(state.searchFilters) > 0;
    if (filteredEmpty) {
      container.innerHTML = `
        <div class="swipe-empty">
          <i data-feather="filter" width="56"></i>
          <h3>No homes match your filters</h3>
          <p>Try broadening your search — you can remove filters one at a time from the chips above, or clear them all.</p>
          <button class="btn btn-primary mt-16" onclick="clearFiltersOnly()">Clear Filters</button>
          <button class="btn btn-secondary mt-16" onclick="openFilterDrawer()">Adjust Filters</button>
        </div>
      `;
      if (actions) actions.style.display = 'none';
      if (window.feather) window.feather.replace();
      return;
    }

    const isForYou = state.feedMode === 'for_you';
    container.innerHTML = `
      <div class="swipe-empty">
        <i data-feather="home" width="56"></i>
        <h3>${isForYou ? "You've seen all your matches!" : 'No new listings right now'}</h3>
        <p>${isForYou ? 'Check the <strong style="color:var(--primary)">New</strong> tab to see everything, or check back later.' : 'New listings appear here as soon as they hit the market.'}</p>
        ${isForYou ? `<button class="btn btn-primary mt-16" onclick="switchFeedMode('new')">Browse New Listings</button>` : ''}
        <button class="btn btn-secondary mt-16" onclick="refreshFeed()">Refresh Feed</button>
      </div>
    `;
    if (actions) actions.style.display = 'none';
    if (window.feather) window.feather.replace();
    return;
  }

  if (actions) actions.style.display = 'flex';

  const listing = state.feed[state.feedIndex];
  const media = getAllMedia(listing);
  const imgUrl = media.length > 0 ? media[0].url : '';

  const tags: string[] = [];
  if (listing.is_turnkey) tags.push('Turnkey');
  if (!listing.hoa) tags.push('No HOA');
  if (listing.sale_type && listing.sale_type !== 'standard') tags.push(listing.sale_type.replace(/_/g, ' '));
  if (listing.property_type) tags.push(listing.property_type.replace(/_/g, ' '));

  let feedBadge = '';
  if (state.feedMode === 'for_you' && listing.match_label) {
    feedBadge = `<div class="swipe-card-badge match-badge">\u2728 ${escapeHtml(listing.match_label)}</div>`;
  } else if (state.feedMode === 'new' && listing.new_label) {
    feedBadge = `<div class="swipe-card-badge new-badge">${escapeHtml(listing.new_label)}</div>`;
  }

  const photoCount = Math.min(media.length, 10);

  // Investor pivot — for investor users we swap the info block and add a
  // score badge on the photo. Everything else in the swipe-card shell
  // stays the same so photos, badges, and gestures keep working.
  // Investor-first: always render the investor card with metrics.
  const infoBlock = renderInvestorInfo(listing);

  container.innerHTML = `
    <div class="swipe-card-wrapper" id="swipe-card-wrapper">
      <div class="swipe-card swipe-card-investor" id="swipe-card">
        ${feedBadge}
        ${renderInvestorScoreBadge(listing)}
        ${photoCount > 1 ? `<div class="carousel-indicators">${media.slice(0, 10).map((_, i) => `<div class="indicator-bar ${i === 0 ? 'active' : ''}"></div>`).join('')}</div>` : ''}
        ${imgUrl
          ? `<img class="swipe-card-image img-loading" src="${escapeHtml(imgUrl)}" alt="Home photo" loading="eager" onload="this.classList.add('loaded')" onerror="imgFallback(this)" id="card-image" data-index="0">`
          : `<img class="swipe-card-image" alt="Home photo" id="card-image" data-index="0" data-empty="true" onload="this.classList.add('loaded')" onerror="imgFallback(this)">`}
        ${photoCount > 1 ? `<div class="photo-counter" id="photo-counter">1 / ${photoCount}</div>` : ''}
        ${(listing.reality_score ?? 0) >= 80 ? '<div class="photo-badge photo-badge-verified"><i data-feather="check-circle" width="12"></i> Verified</div>' : (listing.reality_score ?? 0) >= 50 && (listing.reality_score ?? 0) < 80 ? '<div class="photo-badge photo-badge-enhanced"><i data-feather="alert-circle" width="12"></i> Enhanced</div>' : ''}
        ${listing.next_open_house ? `<div class="open-house-badge swipe-card-oh-badge"><i data-feather="calendar" width="12"></i> Open house \u00B7 ${escapeHtml(formatOpenHouseLabel(listing.next_open_house))}</div>` : ''}
        ${infoBlock}
        <div class="swipe-stamp swipe-stamp-like" id="stamp-like">LIKE</div>
        <div class="swipe-stamp swipe-stamp-nope" id="stamp-nope">NOPE</div>
      </div>
    </div>
  `;

  if (window.feather) window.feather.replace();

  // If the card rendered with an empty img (no usable URL), fall back
  // immediately so the user sees the branded "Photo unavailable" slot
  // instead of an empty box. The img element stays in the DOM so the
  // tap-to-advance logic in setupSwipeGesture can still reassign .src
  // if the user navigates to photo 2, 3, etc.
  const cardImg = document.getElementById('card-image') as HTMLImageElement | null;
  if (cardImg && cardImg.dataset.empty === 'true') {
    imgFallback(cardImg);
  }

  setupSwipeGesture(listing, media);
}

interface MediaEntry {
  url: string;
  sort_order: number;
}

// ── Spring physics for premium swipe feel ────────────────────────────
// Stiffness 350 / damping 40 / mass 1 = the Tinder-class spring config
// from the 2026 fintech UI audit. rAF-driven so it responds to velocity.
function animateSpring(
  from: number, to: number,
  onUpdate: (v: number) => void,
  onDone?: () => void,
  cfg = { stiffness: 350, damping: 40, mass: 1 },
): void {
  let pos = from;
  let vel = 0;
  const dt = 1 / 60;
  function tick() {
    const force = -cfg.stiffness * (pos - to);
    const damp = -cfg.damping * vel;
    vel += ((force + damp) / cfg.mass) * dt;
    pos += vel * dt;
    if (Math.abs(pos - to) < 0.5 && Math.abs(vel) < 0.5) {
      onUpdate(to);
      onDone?.();
      return;
    }
    onUpdate(pos);
    requestAnimationFrame(tick);
  }
  requestAnimationFrame(tick);
}

function setupSwipeGesture(_listing: FeedListing, media: MediaEntry[]): void {
  void _listing;
  const wrapper = document.getElementById('swipe-card-wrapper');
  const card = document.getElementById('swipe-card');
  const stampLike = document.getElementById('stamp-like');
  const stampNope = document.getElementById('stamp-nope');
  const cardImage = document.getElementById('card-image') as HTMLImageElement | null;

  if (!wrapper || !card) return;

  // GPU hints + bottom-center pivot for realistic flick feel.
  wrapper.style.willChange = 'transform';
  wrapper.style.transformOrigin = '50% 100%';

  let startX = 0;
  let currentX = 0;
  let isDragging = false;
  let pointerId: number | null = null;
  let startTime = 0;
  let currentImageIndex = 0;

  const maxIdx = Math.min(media.length, 10) - 1;
  // Photo tap zones — edge 25% left/right navigate the carousel,
  // center 50% opens the deal analysis. The pointerdown/up distance
  // guard keeps drags and swipes from triggering any tap action.
  if (cardImage) {
    cardImage.style.cursor = 'pointer';
    let tapDownX = 0;
    let tapDownY = 0;
    cardImage.addEventListener('pointerdown', (e: PointerEvent) => {
      tapDownX = e.clientX;
      tapDownY = e.clientY;
    });
    cardImage.addEventListener('click', (e: MouseEvent) => {
      if (isDragging) return;
      if (Math.hypot(e.clientX - tapDownX, e.clientY - tapDownY) > 12) return;
      e.stopPropagation();
      const rect = cardImage.getBoundingClientRect();
      const tapX = e.clientX - rect.left;
      if (media.length > 1) {
        const edge = rect.width * 0.25;
        if (tapX < edge) {
          currentImageIndex = Math.max(currentImageIndex - 1, 0);
        } else if (tapX > rect.width - edge) {
          currentImageIndex = Math.min(currentImageIndex + 1, maxIdx);
        } else {
          showCurrentDetail();
          return;
        }
        cardImage.src = media[currentImageIndex].url || '';
        const bars = wrapper.querySelectorAll<HTMLElement>('.indicator-bar');
        bars.forEach((b, i) => b.classList.toggle('active', i === currentImageIndex));
        const counter = document.getElementById('photo-counter');
        if (counter) counter.textContent = `${currentImageIndex + 1} / ${maxIdx + 1}`;
      } else {
        showCurrentDetail();
      }
    });
  }

  // ── Pointer Events API with capture ─────────────────────────────
  function onPointerDown(e: PointerEvent): void {
    if (pointerId !== null) return;
    isDragging = true;
    pointerId = e.pointerId;
    wrapper!.setPointerCapture(e.pointerId);
    startX = e.clientX;
    currentX = 0;
    startTime = Date.now();
    wrapper!.style.transition = 'none';
  }

  function onPointerMove(e: PointerEvent): void {
    if (!isDragging || !wrapper || e.pointerId !== pointerId) return;
    currentX = e.clientX - startX;

    // Rotation proportional to horizontal displacement — max 15°.
    const cardWidth = wrapper.offsetWidth || 300;
    const rotate = (currentX / cardWidth) * 15;
    wrapper.style.transform = `translateX(${currentX}px) rotate(${rotate}deg)`;

    // Stamp opacity tracks drag progress.
    const progress = Math.min(Math.abs(currentX) / (cardWidth * 0.4), 1);
    if (currentX > 0) {
      if (stampLike) stampLike.style.opacity = String(progress);
      if (stampNope) stampNope.style.opacity = '0';
    } else {
      if (stampNope) stampNope.style.opacity = String(progress);
      if (stampLike) stampLike.style.opacity = '0';
    }
  }

  function onPointerUp(e: PointerEvent): void {
    if (!isDragging || !wrapper || e.pointerId !== pointerId) return;
    isDragging = false;
    pointerId = null;
    wrapper.releasePointerCapture(e.pointerId);

    const cardWidth = wrapper.offsetWidth || 300;
    const elapsed = Math.max(1, Date.now() - startTime);
    const velocity = Math.abs(currentX) / elapsed * 1000; // px/s

    // Threshold: 40% of card width OR velocity > 300px/s.
    const thresholdMet = Math.abs(currentX) > cardWidth * 0.4 || velocity > 300;

    if (thresholdMet && Math.abs(currentX) > 30) {
      // Committed swipe — fly off at 130% screen width.
      const direction: 'left' | 'right' = currentX > 0 ? 'right' : 'left';
      const flyX = currentX > 0
        ? window.innerWidth * 1.3
        : -window.innerWidth * 1.3;

      // Haptic at threshold crossing.
      try { navigator.vibrate?.(30); } catch { /* unsupported */ }

      animateSpring(
        currentX, flyX,
        (v) => {
          const r = (v / cardWidth) * 15;
          wrapper!.style.transform = `translateX(${v}px) rotate(${r}deg)`;
        },
        () => handleSwipe(direction),
        { stiffness: 350, damping: 50, mass: 1 },
      );
    } else {
      // Bounce back — bouncier spring for rubber-band snap.
      animateSpring(
        currentX, 0,
        (v) => {
          const r = (v / cardWidth) * 15;
          wrapper!.style.transform = `translateX(${v}px) rotate(${r}deg)`;
        },
        () => {
          wrapper!.style.transform = '';
          if (stampLike) stampLike.style.opacity = '0';
          if (stampNope) stampNope.style.opacity = '0';
        },
        { stiffness: 350, damping: 30, mass: 1 }, // lower damping = more bounce
      );
    }
  }

  function onPointerCancel(e: PointerEvent): void {
    if (e.pointerId !== pointerId) return;
    isDragging = false;
    pointerId = null;
    wrapper!.style.transform = '';
    if (stampLike) stampLike.style.opacity = '0';
    if (stampNope) stampNope.style.opacity = '0';
  }

  // Set touch-action: none on the swipeable area so the browser
  // doesn't intercept horizontal pans for back-navigation.
  wrapper.style.touchAction = 'none';

  wrapper.addEventListener('pointerdown', onPointerDown);
  wrapper.addEventListener('pointermove', onPointerMove);
  wrapper.addEventListener('pointerup', onPointerUp);
  wrapper.addEventListener('pointercancel', onPointerCancel);
}

let _swipeInFlight = false;
export async function handleSwipe(direction: 'left' | 'right'): Promise<void> {
  const listing = state.feed[state.feedIndex];
  if (!listing || _swipeInFlight) return;

  _swipeInFlight = true;
  state.feedIndex++;
  if (state.feeds[state.feedMode]) state.feeds[state.feedMode].index = state.feedIndex;
  saveFeedState();

  state.sessionSwipeCount++;
  const userId = state.user?.id || 'anon';
  sessionStorage.setItem(`sh_sessionSwipeCount_${userId}`, String(state.sessionSwipeCount));

  try {
    await api('POST', '/swipes', { listing_id: listing.id, direction, source: 'feed' });
    if (direction === 'right') {
      showToast('Added to liked homes!', 'success');
    }
  } catch {
    /* swipe recorded locally even if API fails */
  } finally {
    _swipeInFlight = false;
  }

  checkSwipeMilestones();

  renderCurrentCard();
}

export function showCurrentDetail(): void {
  const listing = state.feed[state.feedIndex];
  if (listing) {
    state.selectedListing = listing;
    navigate('detail');
  }
}
